import {Card, MediaCard} from '@jobseeker/lotus/components';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {AdListCategory} from './type';
import {Box, Grid, Link, Typography, useTheme} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {InfoIcon} from '@jobseeker/lotus/assets/icons';
import {useEffect, useState} from 'react';
import {cleanUrl} from '@jobseeker/lotus/utils';
import {courseService} from '@jobseeker/common-core/services';
import {useDispatch} from 'react-redux';
import {saveUserSettings} from '@jobseeker/common-core/reducers/user/thunkServices';
import {enqueueSnackbar} from 'notistack';
import {showErrorToast} from '@jobseeker/common-core/utils';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import Tooltip from '@jobseeker/lotus/elements/Tooltip';
import Checkbox from '@jobseeker/lotus/elements/CheckBox';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import CourseMetadata from './CourseMetadata';

const ListSimilarCourses = (props: {
  similarCourses: AdListCategory[];
  similarCourseEnquiryEnabled?: boolean;
  removeSimilarCourses: () => void;
}) => {
  const {
    similarCourses,
    similarCourseEnquiryEnabled = false,
    removeSimilarCourses,
  } = props;

  const [selectedCourses, setSelectedCourses] = useState<
    {id: number; selected: boolean}[]
  >(
    similarCourses.map(course => ({
      id: course.metadata[0].courseId,
      selected: true,
    }))
  );
  const [enableSimilarCourseAutoEnquiry, setEnableSimilarCourseAutoEnquiry] =
    useState(true);
  const [showSimilarCoursesList, setShowSimilarCoursesList] = useState(true);
  const theme = useTheme();

  const queryParams = new URLSearchParams(window?.location?.search);

  const refinv = queryParams.get('refinv') ?? '';
  const reforg = queryParams.get('reforg') ?? '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: any = useDispatch();

  const isDeviceLarge = useIsLargeDevice(1024);

  const saveSettings = (name: string, value: 0 | 1) => {
    dispatch(
      saveUserSettings({
        accountSettings: {[name]: value},
      })
    );
  };
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const handleEnquireButtonClick = async (hideCourses = true) => {
    const courses = selectedCourses
      .filter(metata => metata.selected)
      .map(course => course.id);

    if (courses.length > 0) {
      saveSettings(
        'similarCourseEnquiry',
        enableSimilarCourseAutoEnquiry ? 1 : 0
      );
    }

    const enquiryApiResponse = await courseService.enquireMultipleCourses({
      coursePublishedIds: courses,
      toSkipIds: courses,
      refinv,
      reforg,
      device: sessionStorage.getItem('deviceType') || 'Web',
    });

    if (
      Object.hasOwn(enquiryApiResponse, 'error') ||
      Object.keys(enquiryApiResponse?.CourseMessages ?? {}).length === 0
    ) {
      showErrorToast(
        enquiryApiResponse?.error?.message ??
          'Some error occurred, please try again',
        enquiryApiResponse?.error
      );
      return;
    }
    enqueueSnackbar('The courses have been sucessfully enquired', {
      variant: 'success',
    });

    if (hideCourses) {
      setShowSimilarCoursesList(false);
    }
  };

  useEffect(() => {
    if (!similarCourseEnquiryEnabled) {
      return;
    }
    handleEnquireButtonClick(false);
  }, [similarCourseEnquiryEnabled]);

  if (!showSimilarCoursesList) {
    return <></>;
  }

  return (
    <Card
      variant={VersionVariants.VERSION_TWO}
      heading={
        <Typography
          fontSize={theme.typography.pxToRem(20)}
          fontWeight={isAppIimjobs ? 500 : 700}
          ml={isAppIimjobs ? 2 : 4.5}
          mb={isAppIimjobs ? 2 : 1}
        >
          {similarCourseEnquiryEnabled ? '' : 'Review more courses'}
        </Typography>
      }
      sx={{
        px: 0,
        pt: isAppIimjobs ? 3 : 0,
        pb: similarCourseEnquiryEnabled ? 0 : 2,
      }}
    >
      {!similarCourseEnquiryEnabled && (
        <Typography
          mx={isAppIimjobs ? 2 : 4.5}
          mt={{xxs: 1, md: 0.5}}
          fontSize={isAppIimjobs ? 12 : 16}
          fontWeight={400}
          color="text.primary"
          lineHeight={'16px'}
        >
          Enquire about more similar courses, evaluate the different options and
          choose the right course for yourself.
        </Typography>
      )}
      {similarCourseEnquiryEnabled && (
        <>
          <Box
            border={'2px solid #ffffff'}
            minHeight={90}
            display={{xxs: 'block', md: isAppIimjobs ? 'flex' : 'block'}}
            sx={{
              borderRadius: 4,
              px: {xxs: 1, md: 1.5},
              py: 0,
              overflow: 'hidden',
              background: {
                xxs: 'linear-gradient(201deg, rgba(255, 255, 255, 1) 36%, rgba(255,255,255,0) 35%)',
                md: 'linear-gradient(201deg, rgb(255, 255, 255) 54%, rgba(255, 255, 255, 0) 35%)',
                lg: 'linear-gradient(201deg, rgba(255, 255, 255, 1) 62%, rgba(255,255,255,0) 35%)',
              },
            }}
          >
            <Box
              display={'flex'}
              mr={{xxs: 0, md: 6}}
              justifyContent={'space-between'}
            >
              <Box display={'block'} mt={{xss: 0.5, md: 1.5}}>
                <Typography
                  variant="subtitle2"
                  mb={isAppIimjobs ? '-5px' : '12px'}
                  ml={isAppIimjobs ? 0 : 2.5}
                  sx={{
                    fontSize: !isAppIimjobs ? '16px' : '14px',
                    fontWeight: isAppIimjobs ? 500 : 700,
                  }}
                >
                  Similar Courses
                  {isAppIimjobs ? <br /> : <> </>}
                  enquired
                </Typography>
              </Box>
            </Box>
            <Box
              gap={1.5}
              display={
                isAppIimjobs ? 'flex' : {xxs: 'inline-block', md: 'flex'}
              }
              py={{xxs: 1, md: 0}}
              width={
                isAppIimjobs ? {sm: '75%', md: '70%', lg: '77%'} : {xxs: '100%'}
              }
              sx={{
                'overflow-x': 'scroll',
                '-ms-overflow-style': 'none',
                'scrollbar-width': 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              {similarCourses?.map(({metadata: [course]}, index) => (
                <Link
                  key={`job-page-media-card-${index}`}
                  href={`/course/${cleanUrl(course.Title)}-${
                    course.courseId
                  }?reforg=sc&refinv=scc`}
                  target="_blank"
                  sx={{textDecoration: 'none', width: '48%'}}
                >
                  {isAppIimjobs ? (
                    <Box width={300} minWidth={300}>
                      <MediaCard
                        logoUrl={course['Image Url']}
                        variant={VersionVariants.VERSION_TWO}
                        title={course.oneLiner}
                        subtitle={course?.Title?.split('|')[0]?.trim()}
                        sx={{
                          maxHeight: 66,
                          borderColor: 'border.main',
                        }}
                      />
                    </Box>
                  ) : (
                    <>
                      <Box
                        display={{xxs: 'inline-block', md: 'flex'}}
                        gap={1}
                        sx={{
                          width: {xxs: '85%', md: '100%'},
                          border: '1px solid #e5e1e1',
                          borderRadius: 2,
                          alignItems: 'start',
                          mx: 2,
                          py: 2,
                          px: 2,
                          mb: {xxs: 2, md: 0},
                          height: {xxs: 200, md: 120},
                        }}
                      >
                        <Box
                          display={'flex'}
                          width={{xxs: '100%', md: '90%'}}
                          gap={1}
                        >
                          <Image
                            src={course['Image Url']}
                            height={50}
                            width={50}
                            alt="course-logo"
                            style={{
                              borderRadius: '8px',
                              border: '0.6px solid #7E7E7E33',
                            }}
                          />
                          <Box
                            display={'block'}
                            sx={{
                              width: {xxs: '60%', md: '90%'},
                              height: '100%',
                            }}
                          >
                            <Typography
                              sx={{
                                width: '75%',
                                fontSize: 18,
                                fontWeight: 700,
                                color: '#000',
                              }}
                            >
                              {course.institute}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 400,
                                color: '#000',
                              }}
                            >
                              {course.Title}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}
                </Link>
              ))}
            </Box>
          </Box>
          <Typography
            m={2}
            mt={isAppIimjobs ? 0 : 2}
            fontSize={theme.typography.pxToRem(14)}
            ml={isAppIimjobs ? 2 : 4}
            mb={isAppIimjobs ? 2 : 5}
          >
            To change your preference for enquiry of similar courses, go to{' '}
            <Link
              href={`${
                process.env.NEXT_PUBLIC_LOAD_CONFIG
              }/settings?section=other${isAppIimjobs ? 's' : ''}`}
              target="_blank"
              sx={{
                textDecoration: isAppIimjobs ? 'none' : 'underline',
                color: isAppIimjobs ? theme.palette.primary.main : '#000',

                ':hover': {
                  color: isAppIimjobs ? theme.palette.primary.main : '#000',
                },
              }}
            >
              Settings &gt; Others &gt; Allow similar course enquiry
            </Link>
          </Typography>
        </>
      )}
      {!similarCourseEnquiryEnabled && (
        <Box ml={2}>
          <Grid container mt={2} mb={3} gap={3}>
            {similarCourses?.map(({metadata: [course]}, index) => (
              <Grid item key={index} sx={{width: '100%'}}>
                {isAppIimjobs ? (
                  <Checkbox
                    size={16}
                    defaultChecked
                    onClick={() => {
                      setSelectedCourses(prev => {
                        prev[index].selected = !prev[index].selected;
                        return prev;
                      });
                    }}
                    sx={{
                      pl: 0.75,
                      alignSelf: 'start',
                    }}
                    label={
                      <Box
                        display="flex"
                        gap={1}
                        sx={{
                          alignItems: 'start',
                          pr: 2,
                        }}
                      >
                        <Image
                          src={course['Image Url']}
                          height={50}
                          width={50}
                          alt="course-logo"
                          style={{
                            borderRadius: '8px',
                            border: '0.6px solid #7E7E7E33',
                          }}
                        />
                        <Box>
                          <Link
                            href={
                              new URL(
                                `/course/${cleanUrl(course.Title)}-${
                                  course.courseId
                                }?refinv=scc&reforg=sc`,
                                process.env.NEXT_PUBLIC_LOAD_CONFIG
                              ).href
                            }
                            sx={{
                              p: 0,
                              fontSize: isDeviceLarge ? 14 : 12,
                              color: theme.palette.secondary.dark,
                              fontWeight: 500,
                              textDecoration: 'none',
                            }}
                          >
                            {course.Title}
                          </Link>
                          {isDeviceLarge && (
                            <CourseMetadata
                              course={course}
                              isDeviceLarge={isDeviceLarge}
                            />
                          )}
                        </Box>
                      </Box>
                    }
                  />
                ) : (
                  <Box width={'100%'} mt={'24px'}>
                    <Checkbox
                      variant={VersionVariants.VERSION_HIRIST}
                      fullWidth
                      size={16}
                      defaultChecked
                      onClick={() => {
                        setSelectedCourses(prev => {
                          prev[index].selected = !prev[index].selected;
                          return prev;
                        });
                      }}
                      sx={{
                        pl: 4,

                        alignSelf: 'start',
                        '& .MuiFormControlLabel-label': {
                          width: '100%',
                        },
                      }}
                      label={
                        <Link
                          href={
                            new URL(
                              `/course/${cleanUrl(course.Title)}-${
                                course.courseId
                              }?refinv=scc&reforg=sc`,
                              process.env.NEXT_PUBLIC_LOAD_CONFIG
                            ).href
                          }
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              width: '100%',
                            },
                            p: 0,
                            fontSize: isDeviceLarge ? 14 : 12,
                            color: theme.palette.secondary.dark,
                            fontWeight: 500,
                            textDecoration: 'none',
                            width: '100%',
                          }}
                        >
                          <Box
                            display={{xxs: 'inline-block', md: 'flex'}}
                            gap={1}
                            sx={{
                              width: 'calc(100% - 32px)',
                              border: '1px solid #e5e1e1',
                              borderRadius: 2,
                              alignItems: 'start',
                              mx: 2,
                              py: 2,
                              px: 2,
                            }}
                          >
                            <Box
                              display={'flex'}
                              width={{xxs: '100%', md: '60%'}}
                              gap={1}
                            >
                              <Image
                                src={course['Image Url']}
                                height={50}
                                width={50}
                                alt="course-logo"
                                style={{
                                  borderRadius: '8px',
                                  border: '0.6px solid #7E7E7E33',
                                }}
                              />
                              <Box
                                display={'block'}
                                sx={{
                                  width: {xxs: '60%', md: '90%'},
                                  height: '100%',
                                }}
                              >
                                <Typography
                                  sx={{
                                    width: '75%',
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color: '#000',
                                  }}
                                >
                                  {course.institute}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    fontWeight: 400,
                                    color: '#000',
                                  }}
                                >
                                  {course.Title}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                width: '0.6px',
                                backgroundColor: '#7E7E7E33',
                                height: '125px',
                                display: {xxs: 'none', md: 'flex'},
                                mt: -2,
                                mb: -2,
                                mr: 1,
                                alignItems: 'start',

                                alignSelf: 'stretch', // Stretches inside a flex container
                              }}
                            ></Box>
                            {
                              <CourseMetadata
                                course={course}
                                isDeviceLarge={isDeviceLarge}
                              />
                            }
                          </Box>
                        </Link>
                      }
                    />
                  </Box>
                )}
                {!isDeviceLarge && isAppIimjobs && (
                  <CourseMetadata
                    course={course}
                    isDeviceLarge={isDeviceLarge}
                  />
                )}
              </Grid>
            ))}
          </Grid>

          <Box display="flex" alignItems="center" mb={1}>
            <Checkbox
              variant={
                isAppIimjobs
                  ? VersionVariants.VERSION_TWO
                  : VersionVariants.VERSION_HIRIST
              }
              fullWidth={isAppIimjobs ? false : true}
              size={16}
              defaultChecked
              onClick={() => {
                setEnableSimilarCourseAutoEnquiry(
                  !enableSimilarCourseAutoEnquiry
                );
              }}
              sx={{
                pl: isAppIimjobs ? 0.75 : 4.5,
              }}
              label={
                <Typography
                  variant="body1"
                  display="inline"
                  fontSize={14}
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: '24px',
                    ml: isAppIimjobs ? 0 : 2,
                  }}
                >
                  Always share my interest for similar courses{' '}
                  <Tooltip
                    placement="bottom-end"
                    arrow
                    title={
                      <Typography
                        variant="subtitle3"
                        lineHeight="16px"
                        sx={{
                          whiteSpace: 'normal',
                          overflowWrap: 'break-word',
                        }}
                      >
                        With this feature, whenever you enquire a course, we
                        enquire a similar course for you on your behalf, you can
                        evaluate both the courses and choose the right course
                        for yourself. (You can change this setting anytime by
                        visiting the &apos;Settings&apos; Page)
                      </Typography>
                    }
                  >
                    <Image
                      alt="info"
                      src={InfoIcon}
                      height={14}
                      width={14}
                      style={{
                        display: 'inline-block',
                        verticalAlign: 'middle',
                        marginLeft: 4,
                      }}
                    />
                  </Tooltip>
                </Typography>
              }
            />
          </Box>
          <Box
            display="flex"
            flexDirection={!isAppIimjobs && !isDeviceLarge ? 'column' : 'row'}
            gap={2}
            mt={2}
            mb={1}
            mx={isAppIimjobs ? 2 : {xxs: 2, md: 3}}
            justifyContent={{
              md: 'flex-start',
              xxs: !isAppIimjobs ? 'center' : 'space-between',
            }}
          >
            <Button
              variant="contained"
              label="use_app"
              size="small"
              data-testid="use_app"
              sx={{
                fontSize: isAppIimjobs ? {xs: 12, md: 14} : 16,
                color: isAppIimjobs ? theme.palette.primary.main : '#fff',
                bgcolor: isAppIimjobs
                  ? 'transparent'
                  : theme.palette.primary.main,
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: isAppIimjobs ? theme.spacing(5) : '3px',
                px: isAppIimjobs
                  ? {xs: 1.75, md: 2}
                  : {xxs: '16px', md: '21px'},
                py: isAppIimjobs ? {xs: 1, md: 1.125} : '13px',
                ':hover': {
                  bgcolor: isAppIimjobs
                    ? 'transparent'
                    : theme.palette.primary.main,
                  color: isAppIimjobs ? theme.palette.primary.main : '#fff',
                },
              }}
              onClick={() => handleEnquireButtonClick()}
            >
              Enquire for similar courses
            </Button>
            <Button
              variant="text"
              sx={{
                fontSize: isAppIimjobs ? {xs: 12, md: 14} : 16,
                px: 1.375,
                py: 0.625,
                ':hover': {bgcolor: 'transparent'},
              }}
              onClick={removeSimilarCourses}
            >
              Not Now
            </Button>
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default ListSimilarCourses;
